import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { money, fmtDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Gift } from "lucide-react";
import { toast } from "sonner";

export default function PrizeCodes() {
  const [codes, setCodes] = useState([]);
  const [products, setProducts] = useState([]);
  const [variant, setVariant] = useState("");
  const [count, setCount] = useState("1");

  const load = useCallback(() => {
    api.get("/prize-codes").then((r) => setCodes(r.data)).catch(() => {});
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const variantOpts = products.flatMap((p) => (p.variants || []).map((v) => ({ id: v.id, label: `${p.name} · ${v.label} (${money(v.price)})` })));

  const generate = () => {
    if (!variant) { toast.error("Select a product variant"); return; }
    api.post("/prize-codes", { variant_id: variant, count: +count }).then(() => { toast.success("Codes generated"); load(); }).catch((e) => toast.error(formatApiError(e)));
  };

  return (
    <div className="max-w-4xl space-y-5">
      <Card className="p-5 space-y-3">
        <div className="flex items-center gap-2 font-semibold text-slate-800"><Gift className="w-5 h-5 text-amber-600" />Generate prize codes</div>
        <div className="grid md:grid-cols-3 gap-3 items-end">
          <div className="md:col-span-2"><Label className="text-xs">Product variant</Label>
            <Select value={variant} onValueChange={setVariant}><SelectTrigger data-testid="prize-variant-select"><SelectValue placeholder="Select variant" /></SelectTrigger>
              <SelectContent>{variantOpts.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}</SelectContent></Select>
          </div>
          <div className="flex gap-2"><div className="flex-1"><Label className="text-xs">Count</Label><Input type="number" value={count} onChange={(e) => setCount(e.target.value)} data-testid="prize-count-input" /></div>
            <Button onClick={generate} className="bg-amber-600 hover:bg-amber-700 self-end" data-testid="generate-prize-btn">Generate</Button></div>
        </div>
      </Card>

      <Card className="divide-y">
        {codes.map((c) => (
          <div key={c.id} className="flex items-center gap-3 p-3 text-sm" data-testid={`prize-code-${c.id}`}>
            <span className="font-mono font-semibold">{c.code}</span>
            <div className="flex-1 text-slate-600">{c.product_name} · retail {money(c.retail)}</div>
            {c.redeemed ? <Badge variant="secondary">Redeemed by {c.redeemed_by}</Badge> : <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>}
            <span className="text-xs text-slate-400">{fmtDate(c.created_at)}</span>
          </div>
        ))}
        {codes.length === 0 && <div className="p-8 text-center text-slate-400 text-sm">No prize codes generated yet.</div>}
      </Card>
    </div>
  );
}
